import { useMemo } from "react";
import { Box, LinearProgress, MenuItem, Skeleton, Stack, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from "recharts";
import EmptyState from "./EmptyState";

const isoDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || "")) &&
  !Number.isNaN(Date.parse(`${value}T00:00:00Z`)) &&
  new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value;

export const completeDailySeries = (rows, period, dateKey = "day", countKey = "count") => {
  if (!Array.isArray(rows) || !isoDate(period?.starts_on) || !isoDate(period?.ends_on)) return null;
  const start = Date.parse(`${period.starts_on}T00:00:00Z`);
  const end = Date.parse(`${period.ends_on}T00:00:00Z`);
  const days = Math.round((end - start) / 86400000) + 1;
  if (!Number.isFinite(days) || days < 1 || days > 366) return null;
  const counts = new Map();
  for (const row of rows) {
    const day = row?.[dateKey];
    const count = row?.[countKey];
    if (!isoDate(day) || day < period.starts_on || day > period.ends_on || counts.has(day) || !Number.isSafeInteger(count) || count < 0) return null;
    counts.set(day, count);
  }
  return Array.from({ length: days }, (_, index) => {
    const day = new Date(start + index * 86400000).toISOString().slice(0, 10);
    return { day, count: counts.get(day) ?? 0 };
  });
};

export const AnalyticsChartContainer = ({ title, description, period, action, loading, error, empty, children, height = 290 }) => (
  <Box component="section" sx={{ minWidth: 0, py: 2, borderTop: "1px solid", borderColor: "divider" }}>
    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={1} sx={{ mb: 2 }}>
      <Box minWidth={0}>
        <Typography variant="h6" fontWeight={700}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">{description}</Typography>
        {period && <Typography variant="caption" color="text.secondary">{period}</Typography>}
      </Box>
      {action}
    </Stack>
    {loading ? <Skeleton variant="rectangular" height={height} /> : error ? (
      <EmptyState compact title={error} />
    ) : empty ? <EmptyState compact title={empty} /> : children}
  </Box>
);

const CountTooltip = ({ active, payload, label, unit, locale }) => {
  if (!active || !payload?.length) return null;
  return <Box sx={{ p: 1.25, bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 1, boxShadow: 4 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={700}>{new Intl.NumberFormat(locale).format(payload[0].value)} {unit}</Typography>
  </Box>;
};

export const TimeSeriesChart = ({ rows, period, dateKey = "day", countKey = "count", unit, locale, onSelectDay, selectLabel, emptyLabel }) => {
  const theme = useTheme();
  const series = useMemo(() => completeDailySeries(rows, period, dateKey, countKey), [rows, period, dateKey, countKey]);
  if (!series) return <EmptyState compact title={emptyLabel} />;
  if (!series.some((row) => row.count > 0)) return <EmptyState compact title={emptyLabel} />;
  return <Stack spacing={1.5}>
    <Box sx={{ width: "100%", height: 260, minWidth: 0 }} role="img" aria-label={`${unit}: ${period.starts_on} - ${period.ends_on}`}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={series} margin={{ top: 12, right: 10, left: -18, bottom: 0 }} onClick={(state) => { if (state?.activeLabel && onSelectDay) onSelectDay(state.activeLabel); }}>
          <CartesianGrid stroke={theme.palette.divider} strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="day" tick={{ fill: theme.palette.text.secondary, fontSize: 11 }} tickLine={false} axisLine={false} minTickGap={22} />
          <YAxis allowDecimals={false} tick={{ fill: theme.palette.text.secondary, fontSize: 11 }} tickLine={false} axisLine={false} />
          <ChartTooltip content={<CountTooltip unit={unit} locale={locale} />} />
          <Bar dataKey="count" name={unit} fill={theme.palette.primary.main} maxBarSize={34} radius={[3, 3, 0, 0]} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </Box>
    {onSelectDay && <TextField select size="small" label={selectLabel} value="" onChange={(event) => onSelectDay(event.target.value)} sx={{ width: { xs: "100%", sm: 210 } }}>
      <MenuItem value="" disabled>{selectLabel}</MenuItem>
      {series.map((row) => <MenuItem key={row.day} value={row.day}>{row.day} - {new Intl.NumberFormat(locale).format(row.count)}</MenuItem>)}
    </TextField>}
  </Stack>;
};

export const BreakdownChart = ({ rows, total, getKey, getLabel, getCount, getHref, colorFor, locale, unit, emptyLabel }) => {
  const theme = useTheme();
  if (!Array.isArray(rows) || rows.length === 0) return <EmptyState compact title={emptyLabel} />;
  const max = Math.max(...rows.map((row) => typeof getCount(row) === "number" && Number.isFinite(getCount(row)) ? getCount(row) : 0), 1);
  return <Stack spacing={0.75}>
    {rows.map((row) => {
      const count = getCount(row);
      if (typeof count !== "number" || !Number.isSafeInteger(count) || count < 0) return null;
      const label = getLabel(row);
      const href = getHref?.(row);
      const percentage = typeof total === "number" && Number.isSafeInteger(total) && total >= count && total > 0 ? Math.round((count / total) * 100) : null;
      const color = colorFor?.(row) || theme.palette.primary.main;
      const body = <>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
          <Typography variant="body2" fontWeight={600} noWrap>{label}</Typography>
          <Tooltip title={`${new Intl.NumberFormat(locale).format(count)} ${unit}${percentage === null ? "" : ` (${percentage}%)`}`}>
            <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>{new Intl.NumberFormat(locale).format(count)}{percentage === null ? "" : ` | ${percentage}%`}</Typography>
          </Tooltip>
        </Stack>
        <LinearProgress variant="determinate" value={(count / max) * 100} sx={{ height: 5, mt: 0.7, borderRadius: 1, bgcolor: "action.hover", "& .MuiLinearProgress-bar": { bgcolor: color } }} />
      </>;
      const sx = { display: "block", p: 1, borderRadius: 1, color: "text.primary", textDecoration: "none", "&:hover": { bgcolor: "action.hover" }, "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}` } };
      return href ? <Box key={getKey(row)} component={RouterLink} to={href} sx={sx}>{body}</Box> : <Box key={getKey(row)} sx={sx}>{body}</Box>;
    })}
  </Stack>;
};
